import {ref} from "valtio";

import {IValtioModel} from "@/shared/models/base-model.interface";

/**
 * Base class for Valtio models with subscription management.
 * - Implements IValtioModel (init/dispose optional).
 * - _unsubscribes is stored in ref() so it is not proxied by Valtio when the model is wrapped in proxy().
 * - Use addSubscribe() in init() and call dispose() to clear all subscriptions.
 */
export abstract class BaseValtioModel implements IValtioModel {
    /**
     * Array of unsubscribe functions. Wrapped in ref() so it is not reactive and not proxied.
     */
    protected readonly _unsubscribes = ref<Array<() => void>>([]) as Array<() => void>;

    /**
     * Register an unsubscribe function to be called in dispose().
     * Use for event subscriptions that return a teardown function, e.g. events.on("x", handler).
     */
    protected addSubscribe(unsubscribe: () => void): void {
        this._unsubscribes.push(unsubscribe);
    }

    /**
     * Removes all registered subscriptions. Override dispose() and call super.dispose() to keep cleanup.
     */
    dispose(): void {
        for (const unsubscribe of this._unsubscribes) {
            unsubscribe();
        }
        this._unsubscribes.length = 0;
    }
}
